import React, { Children, useCallback } from 'react'
import { isElement } from 'react-is'

import { useFeatureValue } from '@growthbook/growthbook-react'
import classnames from 'classnames'

import { ShowMore } from '../ShowMore'
import { SinglePadlockCta } from '../SinglePadlockCta'
import { TableModalWrapper } from '../TableModalWrapper'
import styles from './Table.module.scss'
import { formatTableChildren, mapScrollableTable } from './Table.utils'
import { getVariantSettings, variationTableRenderer } from './Variation/utils'

type Props = {
  className: string
  children: React.ReactNode
}

export const Table: React.FC<Props> = (props) => {
  return (
    <TableModalWrapper renderContent={() => <TableRender {...props} />} trackingComponent="Table" />
  )
}

const TableRender: React.FC<Props> = ({ className, children, ...props }) => {
  const tableNoScrolls = String(className).includes('non-scrollable')
  const formattedChildren = formatTableChildren(children)
  const healthWellbeingExperiment = useFeatureValue('whc-1403-health-wellbeing-signup', 'control')
  const isHealthWellbeingVariant =
    healthWellbeingExperiment === 'variant-1' || healthWellbeingExperiment === 'variant-2'

  const variationSettings = getVariantSettings(formattedChildren)
  const hasVariation = !!variationSettings?.type

  const mapChildren = useCallback(
    (childNodes: React.ReactNode, hiddenRows: number | undefined): React.ReactNode =>
      Children.toArray(childNodes).map((child, index) => {
        if (isElement(child)) {
          const Child = child.type
          const hidden = hiddenRows && Child === 'tr' && index >= hiddenRows
          const additionalProps = hidden ? { className: styles.hidden } : {}

          return (
            <Child {...child.props} {...additionalProps}>
              {mapChildren(child.props.children, hiddenRows)}
            </Child>
          )
        }
        return child
      }),
    []
  )

  const wrapTable = useCallback(
    (hiddenRows: number) => {
      return (
        <div className={classnames(styles.tableWrapper, styles.tableControl)}>
          <div className={styles.scrollTableWrapper}>
            <table {...props}>{mapChildren(children, hiddenRows)}</table>
          </div>
          <span className={styles.tableBorder} />
        </div>
      )
    },
    [children, mapChildren, props]
  )
  const tableRenderer = useCallback(
    (list: React.ReactNode, hiddenRows?: number) => {
      if (tableNoScrolls) {
        return wrapTable(hiddenRows || 0)
      } else {
        return mapScrollableTable(list, hiddenRows || 0)
      }
    },
    [tableNoScrolls, wrapTable]
  )
  const showMoreHandler = () => {
    const loadDynSquirrelEvent = new Event('LoadDynSquirrel', { bubbles: true, cancelable: true })

    document.dispatchEvent(loadDynSquirrelEvent)
  }

  return hasVariation ? (
    <>
      {variationTableRenderer({
        formattedChildren,
        variationSettings,
        isHealthWellbeingVariant,
      })}
      <SinglePadlockCta />
    </>
  ) : (
    <ShowMore
      buttonLabel="Show all rows"
      contentRenderer={tableRenderer}
      callback={showMoreHandler}
      className={styles.showMoreWrapper}
      seoFriendly
    >
      {formattedChildren}
    </ShowMore>
  )
}
